import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Grid,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useTheme } from "@mui/material/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { Types } from "../../constants/actionTypes";
import ApiServices from "../../config/ApiServices";
import ApiEndpoint from "../../config/ApiEndpoint";

const inputSx = (isDark) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: isDark ? "#242424" : "#f7fafc",
    borderRadius: "8px",
    "& fieldset": {
      borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0",
    },
    "&:hover fieldset": {
      borderColor: isDark ? "#6366f1" : "#1976d2",
    },
    "&.Mui-focused fieldset": {
      borderColor: isDark ? "#818cf8" : "#1976d2",
      borderWidth: "2px",
    },
  },
  "& .MuiInputLabel-root": {
    color: isDark ? "#cbd5e1" : "#718096",
  },
  "& .MuiInputBase-input": {
    color: isDark ? "#ffffff" : "#1a202c",
  },
});

const ResetPasswordNew = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const params = new URLSearchParams(location.search);
  const userId = params.get("uid");
  const email = params.get("email") || "";

  const formik = useFormik({
    initialValues: { password: "", conpassword: "" },
    validationSchema: Yup.object({
      password: Yup.string()
        .required("Password is required.")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
          "Must contain 8 characters, one uppercase, one lowercase, one number and one special character"
        ),
      conpassword: Yup.string()
        .required("Confirm Password is required.")
        .oneOf([Yup.ref("password"), null], "Passwords must match"),
    }),
    onSubmit: async (values) => {
      await handleReset(values);
    },
  });

  const autoLogin = async (password) => {
    if (!email) return false;
    const body = { email, password };
    const headers = { "Content-Type": "application/json" };
    const data = await ApiServices.PostApiCall(
      ApiEndpoint.LOGIN_USER,
      JSON.stringify(body),
      headers
    );
    if (data?.status === true) {
      const normalizedUser = {
        ...(data.data || {}),
        token: data.token,
        user_type: data.data?.user_type || data.user_type,
        role: data.data?.role || data.role,
      };
      props.save_user_data({ user: normalizedUser });
      window.location.href =
        normalizedUser.user_type === "master_org"
          ? "/master-org/dashboard"
          : "/dashboard";
      return true;
    }
    return false;
  };

  const handleReset = async (values) => {
    setLoading(true);
    try {
      const body = { id: Number(userId), password: values.password };
      const headers = { "Content-Type": "application/json" };
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.RESET_PASSWORD,
        JSON.stringify(body),
        headers
      );

      if (data?.status === true) {
        toast.success(data.message || "Password reset successfully");
        const loggedIn = await autoLogin(values.password);
        if (!loggedIn) {
          setTimeout(() => navigate("/"), 1500);
        }
      } else {
        toast.error(data?.message || "Failed to reset password");
      }
    } catch (err) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        backgroundColor: isDark ? "#121212" : "#f5f7fa",
      }}
    >
      <Grid container sx={{ minHeight: "100vh" }}>
        {/* Left Half - Image */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            minHeight: { xs: "220px", md: "100vh" },
            alignItems: "center",
            justifyContent: "center",
            backgroundImage: `url(${process.env.PUBLIC_URL}/loginBG.png)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isDark
                ? "linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.4) 100%)"
                : "linear-gradient(135deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.3) 100%)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.1) 100%)",
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1, textAlign: "center", px: 4 }}>
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/white.png`}
              alt="SportsApp Logo"
              sx={{
                width: { xs: 70, md: 120 },
                height: "auto",
                mb: { xs: 2, md: 4 },
                mx: "auto",
                filter: "drop-shadow(0 8px 24px rgba(0, 0, 0, 0.3))",
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: "1.2rem", md: "1.75rem" },
                fontWeight: 700,
                color: "#ffffff",
                mb: { xs: 1, md: 2 },
                textShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                letterSpacing: "-0.02em",
              }}
            >
              Welcome to SportsApp
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "0.8rem", md: "1.125rem" },
                color: "rgba(255, 255, 255, 0.95)",
                maxWidth: "450px",
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              Manage your clubs and connect with your community
            </Typography>
          </Box>
        </Grid>

        {/* Right Half - Reset Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: { xs: 3, md: 6 },
          }}
        >
          <Container maxWidth="sm" sx={{ width: "100%" }}>
            <Box
              sx={{
                backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
                borderRadius: "24px",
                padding: { xs: 3, md: 5 },
                boxShadow: isDark
                  ? "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)"
                  : "0 20px 60px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04)",
                border: isDark
                  ? "1px solid rgba(255, 255, 255, 0.08)"
                  : "1px solid rgba(0, 0, 0, 0.06)",
                backdropFilter: "blur(10px)",
                maxWidth: "480px",
                mx: "auto",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 4,
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1.25rem", md: "2rem" },
                    fontWeight: 700,
                    color: isDark ? "#ffffff" : "#1a202c",
                    mb: 0.5,
                    letterSpacing: "-0.01em",
                    textAlign: "center",
                  }}
                >
                  Reset Password
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.8rem", md: "0.9375rem" },
                    color: isDark ? "#cbd5e1" : "#718096",
                    fontWeight: 400,
                    textAlign: "center",
                  }}
                >
                  Add and confirm your new password
                </Typography>
              </Box>

              <form onSubmit={formik.handleSubmit}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  {/* New Password */}
                  <TextField
                    fullWidth
                    id="password"
                    name="password"
                    label="New Password"
                    type={showPassword ? "text" : "password"}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: isDark ? "#718096" : "#cbd5e1" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((s) => !s)}
                            edge="end"
                            sx={{
                              color: isDark ? "#718096" : "#cbd5e1",
                              "&:hover": { color: isDark ? "#ffffff" : "#1976d2" },
                            }}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx(isDark)}
                  />

                  {/* Confirm Password */}
                  <TextField
                    fullWidth
                    id="conpassword"
                    name="conpassword"
                    label="Confirm New Password"
                    type={showConfirm ? "text" : "password"}
                    value={formik.values.conpassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.conpassword && Boolean(formik.errors.conpassword)
                    }
                    helperText={formik.touched.conpassword && formik.errors.conpassword}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: isDark ? "#718096" : "#cbd5e1" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirm((s) => !s)}
                            edge="end"
                            sx={{
                              color: isDark ? "#718096" : "#cbd5e1",
                              "&:hover": { color: isDark ? "#ffffff" : "#1976d2" },
                            }}
                          >
                            {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx(isDark)}
                  />

                  {/* Submit */}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      mt: 2,
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: 600,
                      borderRadius: "8px",
                      textTransform: "none",
                      backgroundColor: isDark ? "#6366f1" : "#1976d2",
                      boxShadow: isDark
                        ? "0 4px 12px rgba(99, 102, 241, 0.3)"
                        : "0 4px 12px rgba(25, 118, 210, 0.3)",
                      "&:hover": {
                        backgroundColor: isDark ? "#4f46e5" : "#1565c0",
                        boxShadow: isDark
                          ? "0 6px 16px rgba(99, 102, 241, 0.4)"
                          : "0 6px 16px rgba(25, 118, 210, 0.4)",
                        transform: "translateY(-1px)",
                      },
                      "&:disabled": {
                        backgroundColor: isDark ? "#475569" : "#cbd5e1",
                        color: isDark ? "#64748b" : "#94a3b8",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      "Reset Password"
                    )}
                  </Button>
                </Box>
              </form>
            </Box>
          </Container>
        </Grid>
      </Grid>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  profile: state.user.profile,
});

const mapDispatchToProps = (dispatch) => ({
  save_user_data: (data) => dispatch({ type: Types.LOGIN, payload: data }),
});

export default connect(mapStateToProps, mapDispatchToProps)(ResetPasswordNew);
